import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BUILD_CHECK_INTERVAL_MS,
  BUILD_CHECK_MIN_GAP_MS,
  BUILD_CHECK_TIMEOUT_MS,
  BUILD_VERSION_PATH,
} from '../constants/buildUpdate';
import { isNewerBuild, parseBuildVersion, parseVersionFile } from '../utils/buildVersion';

export type VersionFileFetcher = () => Promise<string>;

export interface BuildUpdateCheckOptions {
  runningVersion: string | undefined;
  isEnabled: boolean;
  fetchVersionFile?: VersionFileFetcher;
}

/**
 * Fetches this site's own `/version.txt`, bypassing every cache: the query
 * string defeats the CDN and `no-store` defeats the browser.
 */
export async function fetchDeployedVersionFile(): Promise<string> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), BUILD_CHECK_TIMEOUT_MS);
  try {
    const response = await fetch(`${BUILD_VERSION_PATH}?t=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`GET ${BUILD_VERSION_PATH} returned HTTP ${response.status}`);
    }
    return await response.text();
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * True once the site is serving a strictly newer build than the one running in
 * this tab. Checks on mount, every BUILD_CHECK_INTERVAL_MS, and whenever the tab
 * becomes visible again. Once an update is found it stays found and polling stops.
 */
export function useBuildUpdateCheck({
  runningVersion,
  isEnabled,
  fetchVersionFile = fetchDeployedVersionFile,
}: BuildUpdateCheckOptions): boolean {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const lastCheckAtRef = useRef(0);
  const isCheckingRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (isCheckingRef.current) {
      return;
    }
    isCheckingRef.current = true;
    lastCheckAtRef.current = Date.now();
    try {
      const deployed = parseVersionFile(await fetchVersionFile());
      if (isNewerBuild(deployed, parseBuildVersion(runningVersion))) {
        setIsUpdateAvailable(true);
      }
    } catch (error) {
      // Offline or a transient host error: the next tick retries.
      console.warn('[BuildUpdateCheck] could not read the deployed version', error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [fetchVersionFile, runningVersion]);

  useEffect(() => {
    if (!isEnabled || isUpdateAvailable || !parseBuildVersion(runningVersion)) {
      return undefined;
    }
    void checkForUpdate();
    const intervalId = window.setInterval(() => void checkForUpdate(), BUILD_CHECK_INTERVAL_MS);
    const handleVisibilityChange = () => {
      const isDue = Date.now() - lastCheckAtRef.current >= BUILD_CHECK_MIN_GAP_MS;
      if (document.visibilityState === 'visible' && isDue) {
        void checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate, isEnabled, isUpdateAvailable, runningVersion]);

  return isUpdateAvailable;
}
